import { Routes } from 'common/constants/routes';
import { ColumnDefinition } from 'common/types/TypeDefs';

const STORAGE_KEY = 'palmColumnVisibility';
const DYNAMIC_SEGMENT = ':id';

/*
    Only the columns a user has explicitly toggled are stored. Anything absent
    falls back to the column's code-defined default, which is what keeps a newly
    added column hidden until the user asks for it, and what lets Reset work by
    simply dropping the grid's entry.
*/
export type ColumnVisibilityModel = Record<string, boolean>;
export type StoredColumnVisibility = Record<string, ColumnVisibilityModel>;

/*
    Every static segment the app's routes are built from. Any other segment in a
    pathname is a resource identifier (a guid, an opisnet username, ...) and is
    normalized away, so one grid keeps a single setting rather than a separate
    one for every tenant or suite it happens to be viewed under.
*/
const staticRouteSegments = new Set(
    Object.values(Routes)
        .flatMap(route => route.split('/'))
        .filter(segment => segment.length > 0 && !segment.startsWith(':'))
);

export const buildGridKey = (pathname: string, title: string): string => {
    const normalizedPath = pathname
        .split('/')
        .filter(segment => segment.length > 0)
        .map(segment => staticRouteSegments.has(segment) ? segment : DYNAMIC_SEGMENT)
        .join('/');

    return `${normalizedPath}/${title}`;
};

export const readAllStoredVisibility = (): StoredColumnVisibility => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        const parsed = stored ? JSON.parse(stored) : null;

        return parsed && typeof parsed === 'object' ? parsed as StoredColumnVisibility : {};
    } catch {
        // A corrupt or unavailable cache must never stop a grid from rendering.
        return {};
    }
};

const writeAllStoredVisibility = (allGrids: StoredColumnVisibility) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(allGrids));
    } catch {
        // Quota or private-mode failures leave the choice applied for this session only.
    }
};

export const writeStoredVisibility = (gridKey: string, overrides: ColumnVisibilityModel) => {
    writeAllStoredVisibility({ ...readAllStoredVisibility(), [gridKey]: overrides });
};

export const clearStoredVisibility = (gridKey: string) => {
    const allGrids = readAllStoredVisibility();
    delete allGrids[gridKey];
    writeAllStoredVisibility(allGrids);
};

export const isVisibleByDefault = ({ defaultHidden }: ColumnDefinition): boolean => !defaultHidden;

export const differsFromDefaults = (
    columnDefinitions: ColumnDefinition[],
    model: ColumnVisibilityModel
): boolean => columnDefinitions.some(column => !!model[column.columnId] !== isVisibleByDefault(column));

export const resolveVisibilityModel = (
    columnDefinitions: ColumnDefinition[],
    overrides: ColumnVisibilityModel
): ColumnVisibilityModel => Object.fromEntries(
    columnDefinitions.map(column => {
        const storedOverride = overrides[column.columnId];

        return [column.columnId, typeof storedOverride === 'boolean' ? storedOverride : isVisibleByDefault(column)];
    })
);

/*
    Keeps the cache minimal by storing only what differs from the defaults, so a
    grid the user has reset back to default holds no entry at all.
*/
export const extractOverrides = (
    columnDefinitions: ColumnDefinition[],
    model: ColumnVisibilityModel
): ColumnVisibilityModel => Object.fromEntries(
    columnDefinitions
        .filter(column => model[column.columnId] !== isVisibleByDefault(column))
        .map(column => [column.columnId, model[column.columnId]])
);
